/**
 * The editable shape of a contact, and the conversions either side of
 * it.
 *
 * Separate from the component that renders them so the editor file
 * exports nothing but a component — React Fast Refresh gives up on a
 * module that mixes the two, and losing hot reload on the form you are
 * actively styling is a real cost.
 */

import type { AccountContact, AccountContactInput } from '../../api/field';

/** Mirrors MAX_CONTACTS_PER_ACCOUNT / MAX_VALUES_PER_CONTACT in the
 *  API's field schemas. Guardrails on hand-typed lists, not business
 *  rules — sized past the real case so they only catch accidents. */
export const MAX_CONTACTS = 10;
export const MAX_VALUES_PER_CONTACT = 5;

/** Deliberately loose: something@something.something. The API runs the
 *  real check; this exists so a typo is caught in the form rather than
 *  coming back as a 422 the rep can't interpret. */
export const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ContactDraft {
  /** Client-only, stable for the life of the row. Not sent. */
  key: string;
  /** Server id when editing an existing contact; absent when new. */
  id?: number;
  name: string;
  role: string;
  phones: string[];
  emails: string[];
}

let draftCounter = 0;
const nextKey = () => `draft-${(draftCounter += 1)}`;

export function emptyContactDraft(): ContactDraft {
  // One empty phone and one empty email box, so both are visible
  // without the rep having to ask for them.
  return { key: nextKey(), name: '', role: '', phones: [''], emails: [''] };
}

/** Server contacts → editable drafts. Always leaves one blank box at
 *  the end of each list so there's somewhere to type. */
export function draftsFromContacts(contacts: AccountContact[]): ContactDraft[] {
  if (contacts.length === 0) return [emptyContactDraft()];
  return contacts.map((c) => ({
    key: nextKey(),
    id: c.id,
    name: c.name ?? '',
    role: c.role ?? '',
    phones: c.phones.length > 0 ? [...c.phones] : [''],
    emails: c.emails.length > 0 ? [...c.emails] : [''],
  }));
}

/** Drafts → request payload. Blank rows go; the server drops cards
 *  that are entirely empty, so an untouched card costs nothing. */
export function draftsToPayload(drafts: ContactDraft[]): AccountContactInput[] {
  return drafts.map((d) => ({
    ...(d.id === undefined ? {} : { id: d.id }),
    name: d.name.trim() || null,
    role: d.role.trim() || null,
    phones: d.phones.map((p) => p.trim()).filter(Boolean),
    emails: d.emails.map((e) => e.trim()).filter(Boolean),
  }));
}

/** True once any typed address is malformed — the caller blocks submit
 *  on it rather than letting the API reject the whole form. */
export function hasInvalidEmail(drafts: ContactDraft[]): boolean {
  return drafts.some((d) =>
    d.emails.some((e) => e.trim().length > 0 && !EMAIL_SHAPE.test(e.trim())),
  );
}
